import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { HiSearch, HiLocationMarker, HiPhone, HiMail, HiUser } from 'react-icons/hi'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair, seoBreadcrumbTriple } from '../../utils/seoUtils'
import './Schools.css'

function Schools() {
  const { t, i18n } = useTranslation()
  const getLocalizedLink = useLocalizedLink()
  const { disciplineId } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [selectedDiscipline, setSelectedDiscipline] = useState(disciplineId || 'all')
  
  const isJapanese = i18n.language === 'ja'
  const isRussian = i18n.language === 'ru'
  
  // Синхронизируем состояние с URL параметром
  useEffect(() => {
    setSelectedDiscipline(disciplineId || 'all')
  }, [disciplineId])
  
  // Обновляем URL при изменении фильтров (только search, disciplineId теперь в пути)
  useEffect(() => {
    const params = new URLSearchParams()
    if (searchTerm) params.set('search', searchTerm)
    setSearchParams(params, { replace: true })
  }, [searchTerm, setSearchParams])

  const getDisciplineName = (id) => {
    const names = {
      kubo: { ru: 'КУБО', en: 'KUBO', ja: 'КУБО' },
      aikido: { ru: 'Айкидо', en: 'Aikido', ja: '合気道' },
      aikijutsu: { ru: 'Айки-дзюцу', en: 'Aiki-jutsu', ja: '合気術' },
      jujutsu: { ru: 'Дзю-дзюцу', en: 'Jujutsu', ja: '柔術' },
      aikibujutsu: { ru: 'Айки-бу-дзюцу', en: 'Aiki-bu-jutsu', ja: '合気武術' },
      karatejutsu: { ru: 'Карате-дзюцу', en: 'Karate-jutsu', ja: '空手術' },
      kenjutsu: { ru: 'Кэн-дзюцу', en: 'Kenjutsu', ja: '剣術' },
      kempo: { ru: 'Кемпо', en: 'Kempo', ja: '拳法' }
    }
    if (id === 'all') return t('schools.allDisciplines', 'All Disciplines')
    const lang = isJapanese ? 'ja' : (isRussian ? 'ru' : 'en')
    return names[id] ? names[id][lang] : id
  }

  const disciplines = ['all', 'kubo', 'aikido', 'aikijutsu', 'jujutsu', 'aikibujutsu', 'karatejutsu', 'kenjutsu', 'kempo']

  // Mock data - в реальном приложении это будет из API
  const allSchools = [
    {
      id: 1,
      name: 'Центр боевых искусств "Цунами"',
      nameEn: 'Tsunami Martial Arts Center',
      nameJa: '武道センター「ツナミ」',
      location: isJapanese ? 'ロシア、スヴェルドロフスク地方、ペルヴォウラリスク市、カルビシェフ通り1番地' : (isRussian ? 'Россия, Свердловская область, г. Первоуральск, ул. Карбышева д.1' : 'Russia, Sverdlovsk Region, Pervouralsk, Karbysheva St., 1'),
      locationRu: 'Россия, Свердловская область, г. Первоуральск, ул. Карбышева д.1',
      locationEn: 'Russia, Sverdlovsk Region, Pervouralsk, Karbysheva St., 1',
      locationJa: 'ロシア、スヴェルドロフスク地方、ペルヴォウラリスク市、カルビシェフ通り1番地',
      disciplines: ['kubo', 'aikido', 'jujutsu', 'karatejutsu'],
      disciplinesRu: ['КУБО', 'Айкидо', 'Дзю-Дзюцу', 'Каратэ-Дзюцу'],
      disciplinesEn: ['KUBO', 'Aikido', 'Jujutsu', 'Karate-jutsu'],
      disciplinesJa: ['КУБО', '合気道', '柔術', '空手術'],
      phone: '89089226022',
      email: 'komars12@yandex.ru',
      trainer: isJapanese ? 'コマロフ・アントン・ヴァレリエヴィチ' : (isRussian ? 'Комаров Антон Валерьевич' : 'Komarov Anton Valeryevich'),
      trainerRu: 'Комаров Антон Валерьевич',
      trainerEn: 'Komarov Anton Valeryevich',
      trainerJa: 'コマロフ・アントン・ヴァレリエヴィチ',
      trainerPhoto: '/images/persons/Komarov_Anton_Valerevich.jpg'
    },
    {
      id: 2,
      name: 'Ассоциация Смешанных Единоборств "Универсальные Бойцы"',
      nameEn: 'Mixed Martial Arts Association "Universal Fighters"',
      nameJa: '総合格闘技協会「ユニバーサルファイターズ」',
      location: isJapanese ? 'ロシア、スヴェルドロフスク地方、ペルヴォウラリスク市、レーニン通り31番地、3階、オフィス322' : (isRussian ? 'Россия, Свердловская область, г. Первоуральск, ул. Ленина д. 31, 3 этаж, офис 322' : 'Russia, Sverdlovsk Region, Pervouralsk, Lenina St., 31, 3rd floor, office 322'),
      locationRu: 'Россия, Свердловская область, г. Первоуральск, ул. Ленина д. 31, 3 этаж, офис 322',
      locationEn: 'Russia, Sverdlovsk Region, Pervouralsk, Lenina St., 31, 3rd floor, office 322',
      locationJa: 'ロシア、スヴェルドロフスク地方、ペルヴォウラリスク市、レーニン通り31番地、3階、オフィス322',
      disciplines: ['jujutsu', 'karatejutsu', 'kubo', 'kempo'],
      disciplinesRu: ['Дзю-Дзюцу', 'Каратэ-Дзюцу', 'КУБО', 'Кемпо'],
      disciplinesEn: ['Jujutsu', 'Karate-jutsu', 'KUBO', 'Kempo'],
      disciplinesJa: ['柔術', '空手術', 'КУБО', '拳法'],
      phone: '8-965-525-77-33',
      email: 'pervomma@yandex.ru',
      trainer: isJapanese ? 'マフネフ・ミハイル・セルゲエヴィチ' : (isRussian ? 'Махнев Михаил Сергеевич' : 'Makhnev Mikhail Sergeevich'),
      trainerRu: 'Махнев Михаил Сергеевич',
      trainerEn: 'Makhnev Mikhail Sergeevich',
      trainerJa: 'マフネフ・ミハイル・セルゲエヴィチ',
      trainerPhoto: '/images/persons/Makhnev_Mikhail_Sergeevich.jpg'
    }
  ]

  // Фильтрация школ
  const filteredSchools = allSchools.filter(school => {
    const schoolName = isJapanese ? (school.nameJa || school.name) : (isRussian ? (school.nameRu || school.name) : (school.nameEn || school.name))
    const schoolLocation = isJapanese ? (school.locationJa || school.location) : (isRussian ? (school.locationRu || school.location) : (school.locationEn || school.location))
    const schoolTrainer = school.trainer ? (isJapanese ? (school.trainerJa || school.trainer) : (isRussian ? (school.trainerRu || school.trainer) : (school.trainerEn || school.trainer))) : null
    
    const matchesSearch = searchTerm === '' || 
      schoolName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      schoolLocation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (schoolTrainer && schoolTrainer.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (school.phone && school.phone.includes(searchTerm)) ||
      (school.email && school.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (school.federation && school.federation.toLowerCase().includes(searchTerm.toLowerCase()))
    
    const matchesDiscipline = selectedDiscipline === 'all' || 
      (school.disciplines && school.disciplines.includes(selectedDiscipline)) ||
      (school.discipline === selectedDiscipline)
    
    return matchesSearch && matchesDiscipline
  })

  const breadcrumbItems = useMemo(() => {
    const home = t('common.home')
    const page = t('common.schools')
    if (!disciplineId || selectedDiscipline === 'all') {
      return seoBreadcrumbPair(home, page, 'schools')
    }
    return seoBreadcrumbTriple(home, page, 'schools', getDisciplineName(disciplineId), `schools/${disciplineId}`)
  }, [t, disciplineId, selectedDiscipline, isJapanese, isRussian])

  return (
    <div className="schools-page">
      <SEO 
        title={t('common.schools')}
        description={t('schools.description', 'Find martial arts schools in SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="schools-header">
          <h1>{t('common.schools')}</h1>
          <p className="page-description">
            {t('schools.intro', 'Find martial arts schools that are members of SUMA International. Search by location, discipline, or federation.')}
          </p>
        </div>

        <div className="schools-filters">
          <div className="search-box">
            <HiSearch className="search-icon" />
            <input
              type="text"
              placeholder={t('schools.searchPlaceholder', 'Search schools...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-select">
            <select
              value={selectedDiscipline}
              onChange={(e) => {
                const newDiscipline = e.target.value
                setSelectedDiscipline(newDiscipline)
                const search = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''
                if (newDiscipline === 'all') {
                  navigate(`${getLocalizedLink('/schools')}${search}`)
                } else {
                  navigate(`${getLocalizedLink('/schools')}/${newDiscipline}${search}`)
                }
              }}
              className="discipline-select"
            >
              <option value="all">{t('schools.allDisciplines', 'All Disciplines')}</option>
              <option value="kubo">{getDisciplineName('kubo')}</option>
              <option value="aikido">{getDisciplineName('aikido')}</option>
              <option value="aikijutsu">{getDisciplineName('aikijutsu')}</option>
              <option value="jujutsu">{getDisciplineName('jujutsu')}</option>
              <option value="aikibujutsu">{getDisciplineName('aikibujutsu')}</option>
              <option value="karatejutsu">{getDisciplineName('karatejutsu')}</option>
              <option value="kenjutsu">{getDisciplineName('kenjutsu')}</option>
              <option value="kempo">{getDisciplineName('kempo')}</option>
            </select>
          </div>
        </div>

        <div className="schools-grid">
          {filteredSchools.length > 0 ? (
            filteredSchools.map(school => {
              const schoolName = isJapanese ? (school.nameJa || school.name) : (isRussian ? (school.nameRu || school.name) : (school.nameEn || school.name))
              const schoolLocation = isJapanese ? (school.locationJa || school.location) : (isRussian ? (school.locationRu || school.location) : (school.locationEn || school.location))
              const schoolTrainer = school.trainer ? (isJapanese ? (school.trainerJa || school.trainer) : (isRussian ? (school.trainerRu || school.trainer) : (school.trainerEn || school.trainer))) : null
              const schoolDisciplines = school.disciplines ? (isJapanese ? school.disciplinesJa : (isRussian ? school.disciplinesRu : school.disciplinesEn)) : null
              const disciplinesToShow = school.disciplines ? school.disciplines : (school.discipline ? [school.discipline] : [])
              
              return (
                <div key={school.id} className="school-card">
                  <div className="school-card-header">
                    {school.logo && (
                      <div className="school-logo">
                        <img src={school.logo} alt={schoolName} />
                      </div>
                    )}
                    <h3 className="school-name">{schoolName}</h3>
                  </div>
                  
                  <div className="school-card-body">
                    <div className="school-location">
                      <HiLocationMarker className="location-icon" />
                      <span>{schoolLocation}</span>
                    </div>
                    
                    {schoolDisciplines && schoolDisciplines.length > 0 && (
                      <div className="school-disciplines">
                        {schoolDisciplines.map((disc, idx) => (
                          <span key={idx} className="discipline-badge">{disc}</span>
                        ))}
                      </div>
                    )}
                    {!schoolDisciplines && disciplinesToShow.length > 0 && (
                      <div className="school-disciplines">
                        <span className="discipline-badge">{getDisciplineName(disciplinesToShow[0])}</span>
                      </div>
                    )}
                    
                    {schoolTrainer && (
                      <div className="school-trainer-section">
                        <div className="school-trainer">
                          {school.trainerPhoto && (
                            <div className="school-trainer-photo">
                              <img src={school.trainerPhoto} alt={schoolTrainer} />
                            </div>
                          )}
                          <div className="school-trainer-info">
                            <span className="school-trainer-label">{t('schools.trainer', 'Тренер')}</span>
                            <span className="school-trainer-name">{schoolTrainer}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {(school.phone || school.email) && (
                    <div className="school-card-footer">
                      <div className="school-contacts">
                        {school.phone && (
                          <a href={`tel:${school.phone}`} className="school-contact-item">
                            <HiPhone className="contact-icon" />
                            <span>{school.phone}</span>
                          </a>
                        )}
                        {school.email && (
                          <a href={`mailto:${school.email}`} className="school-contact-item">
                            <HiMail className="contact-icon" />
                            <span>{school.email}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {school.federation && (
                    <div className="school-federation">
                      <span>{school.federation}</span>
                    </div>
                  )}
                </div>
              )
            })
          ) : (
            <div className="no-schools">
              <p>
                {searchTerm || selectedDiscipline !== 'all' 
                  ? t('schools.noResults', 'No schools found matching your search criteria.') 
                  : t('schools.noSchools', 'No schools found. Schools will be displayed here as they are added to the system.')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Schools
